import { useState } from 'react';
import { useNotify, useDataProvider } from 'react-admin';
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Alert,
    Tab,
    Tabs,
    IconButton,
    Tooltip,
    Chip,
    CircularProgress,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ContentCopyIcon from '@mui/icons-material/ContentCopyOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import VpnKeyIcon from '@mui/icons-material/VpnKeyOutlined';
import AddIcon from '@mui/icons-material/AddOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBackOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTimeOutlined';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOffOutlined';
import SpeedIcon from '@mui/icons-material/SpeedOutlined';
import SecurityIcon from '@mui/icons-material/SecurityOutlined';
import { colors } from '../../theme';

interface GeneratedKeyResult {
    id: number;
    nodeId: string;
    keyPrefix: string;
    apiKey: string;
    createdAt: string;
    expiresAt: string;
}

const lifetimeOptions = [
    { value: 1, label: '1 hour', description: 'Quick test' },
    { value: 8, label: '8 hours', description: 'Recommended', recommended: true },
    { value: 24, label: '24 hours', description: 'Extended session' },
    { value: 168, label: '7 days', description: 'Sprint duration' },
    { value: 336, label: '14 days', description: 'Two-week sprint' },
    { value: 720, label: '30 days', description: 'Long-running work' },
];

const formatRemainingTime = (expiresAt: string): string => {
    const now = new Date();
    const expiry = new Date(expiresAt);
    const diffMs = expiry.getTime() - now.getTime();

    if (diffMs <= 0) return 'Expired';

    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    if (hours >= 24) {
        const days = Math.floor(hours / 24);
        const remainingHours = hours % 24;
        return `${days}d ${remainingHours}h remaining`;
    }
    return hours > 0 ? `${hours}h ${minutes}m remaining` : `${minutes}m remaining`;
};

const helpItems = [
    {
        icon: <AccessTimeIcon />,
        title: 'Short-Lived',
        text: 'Keys expire after the selected duration (1 hour to 30 days). Use the shortest lifetime that fits your development session.',
        color: colors.primary,
    },
    {
        icon: <VisibilityOffIcon />,
        title: 'Shown Once',
        text: 'The API key is only displayed at creation time. Make sure to copy it before leaving this page.',
        color: colors.warning,
    },
    {
        icon: <SpeedIcon />,
        title: 'Rate Limited',
        text: 'You can generate up to 5 keys per 24-hour period. Plan your development sessions accordingly.',
        color: colors.tertiary,
    },
    {
        icon: <SecurityIcon />,
        title: 'Audited',
        text: 'All key generation is logged with your user identity, timestamp, and IP address for security compliance.',
        color: colors.secondary,
    },
];

export const ApiKeyCreate = () => {
    const [nodeId, setNodeId] = useState('');
    const [lifetime, setLifetime] = useState(8);
    const [loading, setLoading] = useState(false);
    const [generatedKey, setGeneratedKey] = useState<GeneratedKeyResult | null>(null);
    const [activeTab, setActiveTab] = useState(0);
    const notify = useNotify();
    const dataProvider = useDataProvider();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!nodeId || nodeId.length !== 8) {
            notify('Node ID must be exactly 8 characters', { type: 'error' });
            return;
        }

        setLoading(true);
        try {
            const result = await dataProvider.create('api-keys', {
                data: { nodeId: nodeId.toUpperCase(), lifetime },
            });
            setGeneratedKey(result.data as GeneratedKeyResult);
            notify('API key generated successfully', { type: 'success' });
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : 'Failed to generate API key';
            notify(message, { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = (text: string, label: string) => {
        navigator.clipboard.writeText(text);
        notify(`${label} copied to clipboard`, { type: 'success' });
    };

    const handleGenerateAnother = () => {
        setGeneratedKey(null);
        setNodeId('');
        setActiveTab(0);
    };

    // Success View
    if (generatedKey) {
        const userSecretsSnippet = `dotnet user-secrets set "XTConnect:ApiKey" "${generatedKey.apiKey}"
dotnet user-secrets set "XTConnect:NodeId" "${generatedKey.nodeId}"`;

        const envVarsSnippet = `export XTCONNECT__APIKEY="${generatedKey.apiKey}"
export XTCONNECT__NODEID="${generatedKey.nodeId}"`;

        const appSettingsSnippet = `{
  "XTConnect": {
    "ApiKey": "${generatedKey.apiKey}",
    "NodeId": "${generatedKey.nodeId}"
  }
}`;

        const configSnippets = [
            { label: 'User Secrets', sublabel: 'Recommended', code: userSecretsSnippet },
            { label: 'Environment Variables', sublabel: '', code: envVarsSnippet },
            { label: 'appsettings.json', sublabel: '', code: appSettingsSnippet },
        ];

        return (
            <Box>
                {/* Page Header */}
                <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                        <Typography sx={{ fontSize: '1.5rem', fontWeight: 700 }}>
                            API Key Generated
                        </Typography>
                        <Chip
                            icon={<CheckCircleOutlineIcon />}
                            label="Success"
                            size="small"
                            sx={{
                                bgcolor: alpha(colors.success, 0.1),
                                color: colors.success,
                                fontWeight: 600,
                            }}
                        />
                    </Box>
                    <Typography variant="body2" sx={{ color: colors.slate[500] }}>
                        Your developer API key has been generated and is ready to use
                    </Typography>
                </Box>

                {/* Warning Banner */}
                <Alert severity="warning" sx={{ mb: 3 }}>
                    <strong>This key will only be shown once.</strong> Copy it now and store it securely.
                    You cannot retrieve it after leaving this page.
                </Alert>

                {/* API Key Display Card */}
                <Card sx={{
                    borderRadius: '12px',
                    border: `1px solid ${colors.slate[100]}`,
                    mb: 3,
                }}>
                    <CardContent sx={{ p: 2.5 }}>
                        <Typography
                            variant="caption"
                            sx={{
                                color: colors.slate[500],
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                fontWeight: 600,
                                fontSize: 11,
                            }}
                        >
                            API Key
                        </Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 1,
                                alignItems: 'center',
                                mt: 1,
                                p: 1.5,
                                bgcolor: colors.slate[50],
                                borderRadius: '8px',
                                border: `1px solid ${colors.slate[200]}`,
                            }}
                        >
                            <Typography
                                sx={{
                                    fontFamily: '"JetBrains Mono", monospace',
                                    fontSize: 13,
                                    fontWeight: 600,
                                    color: colors.slate[900],
                                    flex: 1,
                                    wordBreak: 'break-all',
                                }}
                            >
                                {generatedKey.apiKey}
                            </Typography>
                            <Tooltip title="Copy API Key">
                                <IconButton
                                    onClick={() => copyToClipboard(generatedKey.apiKey, 'API key')}
                                    size="small"
                                    sx={{
                                        color: colors.primary,
                                        '&:hover': { bgcolor: alpha(colors.primary, 0.1) },
                                    }}
                                >
                                    <ContentCopyIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </CardContent>
                </Card>

                {/* Key Details Card */}
                <Card sx={{
                    borderRadius: '12px',
                    border: `1px solid ${colors.slate[100]}`,
                    mb: 3,
                }}>
                    <CardContent sx={{ p: 2.5 }}>
                        <Typography
                            variant="caption"
                            sx={{
                                color: colors.slate[500],
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                fontWeight: 600,
                                fontSize: 11,
                                mb: 2,
                                display: 'block',
                            }}
                        >
                            Key Details
                        </Typography>
                        <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(4, 1fr)',
                            gap: 2,
                        }}>
                            <Box>
                                <Typography variant="caption" sx={{ color: colors.slate[500], display: 'block' }}>
                                    Node ID
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontFamily: '"JetBrains Mono", monospace',
                                        fontWeight: 600,
                                        fontSize: 13,
                                        padding: '4px 8px',
                                        background: colors.slate[100],
                                        borderRadius: '6px',
                                        display: 'inline-block',
                                        mt: 0.5,
                                    }}
                                >
                                    {generatedKey.nodeId}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" sx={{ color: colors.slate[500], display: 'block' }}>
                                    Key Prefix
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontFamily: '"JetBrains Mono", monospace',
                                        fontWeight: 600,
                                        fontSize: 13,
                                        padding: '4px 8px',
                                        background: colors.slate[100],
                                        borderRadius: '6px',
                                        display: 'inline-block',
                                        mt: 0.5,
                                    }}
                                >
                                    {generatedKey.keyPrefix}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" sx={{ color: colors.slate[500], display: 'block' }}>
                                    Created
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 0.5, color: colors.slate[700] }}>
                                    {new Date(generatedKey.createdAt).toLocaleString()}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" sx={{ color: colors.slate[500], display: 'block' }}>
                                    Expires
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 0.5, color: colors.slate[700] }}>
                                    {new Date(generatedKey.expiresAt).toLocaleString()}
                                </Typography>
                                <Chip
                                    label={formatRemainingTime(generatedKey.expiresAt)}
                                    size="small"
                                    sx={{
                                        mt: 0.5,
                                        height: 20,
                                        fontSize: 11,
                                        bgcolor: alpha(colors.warning, 0.1),
                                        color: colors.warning,
                                        fontWeight: 600,
                                    }}
                                />
                            </Box>
                        </Box>
                    </CardContent>
                </Card>

                {/* Configuration Card */}
                <Card sx={{
                    borderRadius: '12px',
                    border: `1px solid ${colors.slate[100]}`,
                    mb: 3,
                }}>
                    <CardContent sx={{ p: 2.5 }}>
                        <Typography
                            variant="caption"
                            sx={{
                                color: colors.slate[500],
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                fontWeight: 600,
                                fontSize: 11,
                                mb: 1.5,
                                display: 'block',
                            }}
                        >
                            Configuration
                        </Typography>
                        <Box sx={{ borderBottom: 1, borderColor: colors.slate[200] }}>
                            <Tabs
                                value={activeTab}
                                onChange={(_, newValue) => setActiveTab(newValue)}
                                variant="fullWidth"
                            >
                                {configSnippets.map((snippet, i) => (
                                    <Tab
                                        key={i}
                                        label={
                                            <Box sx={{ textAlign: 'center' }}>
                                                <span>{snippet.label}</span>
                                                {snippet.sublabel && (
                                                    <Typography variant="caption" sx={{ display: 'block', fontSize: 10, color: colors.tertiary, fontWeight: 600 }}>
                                                        {snippet.sublabel}
                                                    </Typography>
                                                )}
                                            </Box>
                                        }
                                        sx={{ textTransform: 'none', fontSize: 13 }}
                                    />
                                ))}
                            </Tabs>
                        </Box>
                        <Box sx={{ position: 'relative', mt: 0 }}>
                            <Box
                                sx={{
                                    bgcolor: colors.slate[50],
                                    border: `1px solid ${colors.slate[200]}`,
                                    borderTop: 'none',
                                    borderRadius: '0 0 8px 8px',
                                    p: 2,
                                    fontFamily: '"JetBrains Mono", monospace',
                                    fontSize: 12,
                                    color: colors.slate[700],
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-all',
                                }}
                            >
                                {configSnippets[activeTab].code}
                            </Box>
                            <Tooltip title="Copy configuration">
                                <IconButton
                                    onClick={() => copyToClipboard(configSnippets[activeTab].code, 'Configuration')}
                                    size="small"
                                    sx={{
                                        position: 'absolute',
                                        top: 8,
                                        right: 8,
                                        color: colors.primary,
                                        '&:hover': { bgcolor: alpha(colors.primary, 0.1) },
                                    }}
                                >
                                    <ContentCopyIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </CardContent>
                </Card>

                {/* Actions */}
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleGenerateAnother}
                        sx={{
                            boxShadow: 'none',
                            fontWeight: 600,
                            fontSize: 14,
                            borderRadius: '10px',
                            padding: '12px 20px',
                            '&:hover': {
                                boxShadow: '0 4px 12px rgba(8, 145, 178, 0.3)',
                            },
                        }}
                    >
                        Generate Another Key
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        href="/"
                        sx={{
                            fontWeight: 600,
                            fontSize: 14,
                            borderRadius: '10px',
                            padding: '12px 20px',
                        }}
                    >
                        Back to Dashboard
                    </Button>
                </Box>
            </Box>
        );
    }

    // Form View
    return (
        <Box>
            {/* Page Header */}
            <Box sx={{ mb: 3 }}>
                <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, mb: 0.5 }}>
                    Developer API Keys
                </Typography>
                <Typography sx={{ fontSize: 14, color: colors.slate[500] }}>
                    Generate short-lived API keys for local NodeService development
                </Typography>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                {/* Form Card */}
                <Card>
                    <CardContent sx={{ p: 3 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 3 }}>
                            Generate New Key
                        </Typography>

                        <form onSubmit={handleSubmit}>
                            <TextField
                                label="Node ID"
                                value={nodeId}
                                onChange={(e) => setNodeId(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8))}
                                fullWidth
                                required
                                autoFocus
                                placeholder="e.g., TESTNODE"
                                helperText={`${nodeId.length}/8 alphanumeric characters`}
                                error={nodeId.length > 0 && nodeId.length !== 8}
                                inputProps={{
                                    maxLength: 8,
                                    style: { fontFamily: '"JetBrains Mono", monospace', letterSpacing: '0.1em' },
                                }}
                                sx={{ mb: 3 }}
                            />

                            <FormControl fullWidth sx={{ mb: 3 }}>
                                <InputLabel>Key Lifetime</InputLabel>
                                <Select
                                    value={lifetime}
                                    label="Key Lifetime"
                                    onChange={(e) => setLifetime(Number(e.target.value))}
                                >
                                    {lifetimeOptions.map((opt) => (
                                        <MenuItem key={opt.value} value={opt.value}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                                <span>{opt.label}</span>
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        color: opt.recommended ? colors.tertiary : colors.slate[400],
                                                        fontWeight: opt.recommended ? 600 : 400,
                                                    }}
                                                >
                                                    {opt.description}
                                                </Typography>
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <Button
                                type="submit"
                                variant="contained"
                                disabled={loading || nodeId.length !== 8}
                                startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <VpnKeyIcon />}
                                sx={{
                                    boxShadow: 'none',
                                    fontWeight: 600,
                                    fontSize: 14,
                                    borderRadius: '10px',
                                    padding: '12px 20px',
                                    color: '#ffffff',
                                    '&:hover': {
                                        boxShadow: '0 4px 12px rgba(8, 145, 178, 0.3)',
                                    },
                                    '&.Mui-disabled': {
                                        backgroundImage: 'none',
                                        backgroundColor: colors.slate[200],
                                        color: colors.slate[400],
                                    },
                                }}
                            >
                                {loading ? 'Generating...' : 'Generate Key'}
                            </Button>
                        </form>
                    </CardContent>
                </Card>

                {/* Help Panel */}
                <Card sx={{
                    background: `linear-gradient(135deg, ${alpha(colors.primary, 0.05)}, ${alpha(colors.tertiary, 0.05)})`,
                    borderColor: alpha(colors.primary, 0.1),
                }}>
                    <CardContent sx={{ p: 3 }}>
                        <Box sx={{
                            width: 44,
                            height: 44,
                            borderRadius: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundImage: `linear-gradient(135deg, ${colors.primary}, ${colors.tertiary})`,
                            color: 'white',
                            mb: 2,
                            '& svg': { fontSize: 20 },
                        }}>
                            <VpnKeyIcon />
                        </Box>
                        <Typography sx={{ fontSize: 16, fontWeight: 600, mb: 1 }}>
                            Developer API Keys
                        </Typography>
                        <Typography sx={{ fontSize: 14, color: colors.slate[600], mb: 2.5, lineHeight: 1.6 }}>
                            Generate temporary API keys for running NodeService on your local development machine.
                            These keys work with dev/staging environments.
                        </Typography>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {helpItems.map((item) => (
                                <Box key={item.title} sx={{ display: 'flex', gap: 1.5 }}>
                                    <Box sx={{
                                        width: 32,
                                        height: 32,
                                        borderRadius: '8px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        bgcolor: 'white',
                                        color: colors.primary,
                                        flexShrink: 0,
                                        '& svg': { fontSize: 14 },
                                    }}>
                                        {item.icon}
                                    </Box>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                        <Typography sx={{ fontWeight: 600, fontSize: 13, color: colors.slate[800], lineHeight: 1.4 }}>
                                            {item.title}
                                        </Typography>
                                        <Typography sx={{ fontSize: 12, color: colors.slate[500], lineHeight: 1.5 }}>
                                            {item.text}
                                        </Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
};
